import { html } from 'lit-html';
import { get } from '../api.js';
import { app, emptyRow, helpLink, type Page } from '../app.js';
import { barChart, plural } from '../charts.js';
import { duration, pct, when } from '../format.js';
import { delta, num, period, periodName, periodSelect, type AgentMetrics, type HourMetrics, type QueueMetrics, type TeamMetrics } from '../metrics.js';
import { sortHeader, sortRows, type Column, type SortState } from '../table.js';

interface Daily { day: string; timeouts: number; deactivations: number }
interface Offender { agent_id: string; agent_name: string | null; timeouts_7d: string; timeouts_30d: string; timeouts_all_time: string; last_timeout_at: string }

let daily: Daily[] = [];
let hours: HourMetrics[] = [];
let agents: AgentMetrics[] = [];
let team: TeamMetrics | null = null;
let queuesRows: QueueMetrics[] = [];
let offenders: Offender[] = [];
let search = '';
let showAll = false;
const agentSort: SortState = { key: 'timeouts', desc: true };
const queueSort: SortState = { key: 'timeouts', desc: true };
const AGENT_ROWS = 100;

const agentName = (r: { agent_id: string; agent_name: string | null }) => r.agent_name ?? r.agent_id;
export const agentHref = (id: string) => `#/agent/${encodeURIComponent(id)}`;

/** Timeouts compared with the previous window; sorts by the change. */
function changeColumn<T extends { timeouts: number; timeouts_prev: number }>(t: TeamMetrics): Column<T> {
  return {
    key: 'change',
    label: 'Change',
    num: true,
    title: `Timeouts compared with the previous ${periodName()}`,
    value: (r) => (t.prev_covered ? r.timeouts - r.timeouts_prev : null),
    cell: (r) => delta(r.timeouts, r.timeouts_prev, t.prev_covered),
  };
}

/** Deactivation columns appear once enforcement has deactivated someone in the window. */
function enforcementColumns<T extends { deactivations: number; avg_inactive_seconds: number | null }>(t: TeamMetrics): Column<T>[] {
  if (t.deactivations === 0) return [];
  return [
    { key: 'deact', label: 'Deactivations', num: true, value: (r) => r.deactivations, cell: (r) => num(r.deactivations) },
    { key: 'inactive', label: 'Avg inactive', num: true, title: 'Average time deactivated before the agent reactivated the queue', value: (r) => r.avg_inactive_seconds, cell: (r) => duration(r.avg_inactive_seconds) },
  ];
}

function agentColumns(t: TeamMetrics): Column<AgentMetrics>[] {
  return [
    { key: 'name', label: 'Agent', value: agentName, cell: (r) => html`<a href=${agentHref(r.agent_id)}>${agentName(r)}</a>` },
    { key: 'timeouts', label: 'Timeouts', num: true, value: (r) => r.timeouts, cell: (r) => num(r.timeouts) },
    changeColumn(t),
    { key: 'rate', label: 'Rate', num: true, title: 'Timeouts ÷ wrap-ups', value: (r) => r.timeout_rate, cell: (r) => pct(r.timeout_rate) },
    { key: 'wrapups', label: 'Wrap-ups', num: true, value: (r) => r.wrapups, cell: (r) => num(r.wrapups) },
    {
      key: 'avg', label: 'Avg ACW', num: true, title: 'Average time in after-call work over all wrap-ups (a timeout counts as the full time box)',
      value: (r) => r.avg_acw_seconds, cell: (r) => duration(r.avg_acw_seconds),
    },
    {
      key: 'own', label: 'Avg own wrap-up', num: true, title: 'Average ACW on the wrap-ups the agent finished themselves',
      value: (r) => r.avg_own_wrapup_seconds, cell: (r) => duration(r.avg_own_wrapup_seconds),
    },
    {
      key: 'close', label: 'Close calls', num: true, title: `Wrap-ups the agent finished themselves within ${t.close_call_seconds} s of the ACW limit`,
      value: (r) => r.close_calls,
      cell: (r) => html`${num(r.close_calls)}${r.close_calls ? html` <span class="muted">(${pct(r.close_call_rate)})</span>` : ''}`,
    },
    {
      key: 'lost', label: 'ACW in timeouts', num: true, title: 'Time spent in ACW on wrap-ups that timed out',
      value: (r) => r.timeout_acw_seconds, cell: (r) => (r.timeout_acw_seconds ? duration(r.timeout_acw_seconds) : '—'),
    },
    { key: 'last', label: 'Last timeout', value: (r) => r.last_timeout_at, cell: (r) => html`<span class="nowrap">${when(r.last_timeout_at)}</span>` },
    ...enforcementColumns<AgentMetrics>(t),
  ];
}

function queueColumns(t: TeamMetrics): Column<QueueMetrics>[] {
  const name = (r: QueueMetrics) => r.queue_name ?? r.queue_id;
  return [
    { key: 'name', label: 'Queue', value: name, cell: name },
    {
      key: 'limit', label: 'ACW limit', num: true, title: "Learned from the queue's timeouts in the last 30 days: a timeout lasts the full time box",
      value: (r) => r.acw_limit_seconds, cell: (r) => duration(r.acw_limit_seconds),
    },
    { key: 'timeouts', label: 'Timeouts', num: true, value: (r) => r.timeouts, cell: (r) => num(r.timeouts) },
    changeColumn(t),
    { key: 'rate', label: 'Rate', num: true, title: 'Timeouts ÷ wrap-ups', value: (r) => r.timeout_rate, cell: (r) => pct(r.timeout_rate) },
    { key: 'wrapups', label: 'Wrap-ups', num: true, value: (r) => r.wrapups, cell: (r) => num(r.wrapups) },
    { key: 'agents', label: 'Agents timing out', num: true, value: (r) => r.agents_with_timeouts, cell: (r) => num(r.agents_with_timeouts) },
    { key: 'avg', label: 'Avg ACW', num: true, value: (r) => r.avg_acw_seconds, cell: (r) => duration(r.avg_acw_seconds) },
    { key: 'own', label: 'Avg own wrap-up', num: true, value: (r) => r.avg_own_wrapup_seconds, cell: (r) => duration(r.avg_own_wrapup_seconds) },
    { key: 'close', label: 'Close calls', num: true, value: (r) => r.close_calls, cell: (r) => num(r.close_calls) },
    ...enforcementColumns<QueueMetrics>(t),
  ];
}

function teamKpis(t: TeamMetrics) {
  return html`<div class="grid kpis six">
    <div class="kpi"><span class="label">Timeouts</span><span class="value">${num(t.timeouts)}</span>
      <span class="sub">${t.prev_covered ? html`${delta(t.timeouts, t.timeouts_prev, true)} vs previous ${periodName()}` : `No data yet for the previous ${periodName()}`}</span></div>
    <div class="kpi"><span class="label">Timeout rate</span><span class="value">${pct(t.timeout_rate)}</span>
      <span class="sub">of ${num(t.wrapups)} wrap-ups</span></div>
    <div class="kpi"><span class="label">Avg ACW</span><span class="value">${duration(t.avg_acw_seconds)}</span>
      <span class="sub">${duration(t.avg_own_wrapup_seconds)} when agents wrap up themselves</span></div>
    <div class="kpi"><span class="label">Close calls</span><span class="value">${num(t.close_calls)}</span>
      <span class="sub">${pct(t.close_call_rate)} of own wrap-ups, within ${t.close_call_seconds} s of the limit</span></div>
    <div class="kpi"><span class="label">ACW in timeouts</span><span class="value">${duration(t.timeout_acw_seconds)}</span>
      <span class="sub">spent on wrap-ups that timed out</span></div>
    <div class="kpi"><span class="label">Agents timing out</span><span class="value">${num(t.agents_with_timeouts)}</span>
      <span class="sub">of ${num(t.agents)} who wrapped up</span></div>
  </div>`;
}

function dailyChart() {
  return barChart(
    'daily',
    daily.map((d) => ({
      label: d.day.slice(5),
      values: [d.timeouts, d.deactivations],
      tip: html`<strong>${d.day}</strong><span><i class="sw s1"></i>${plural(d.timeouts, 'timeout')}</span><span><i class="sw s2"></i>${plural(d.deactivations, 'deactivation')}</span>`,
    })),
    `Timeouts and deactivations per day, last ${daily.length} days`,
  );
}

const hourLabel = (h: number) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);

/** Timeouts by hour of day, trimmed to the hours with any wrap-ups. */
export function hourChart(id: string, rows: HourMetrics[]) {
  const active = rows.filter((h) => h.wrapups > 0);
  if (!active.length) return html`<p class="muted">No wrap-ups in this period.</p>`;
  const from = active[0]!.hour;
  const to = active[active.length - 1]!.hour;
  return barChart(
    id,
    rows
      .filter((h) => h.hour >= from && h.hour <= to)
      .map((h) => ({
        label: hourLabel(h.hour),
        values: [h.timeouts],
        tip: html`<strong>${hourLabel(h.hour)}–${hourLabel((h.hour + 1) % 24)}</strong>
          <span><i class="sw s1"></i>${plural(h.timeouts, 'timeout')}</span>
          <span>${plural(h.wrapups, 'wrap-up')}, ${pct(h.timeout_rate)} timed out</span>`,
      })),
    'Timeouts by hour of day',
  );
}

export const reports: Page = {
  title: 'Reports',
  async load() {
    const days = period.days;
    const [d, h, a, q, o] = await Promise.all([
      get<{ rows: Daily[] }>(`/reports/daily?days=${Math.max(days, 14)}`),
      get<{ rows: HourMetrics[] }>(`/reports/hours?days=${days}`),
      get<{ rows: AgentMetrics[]; team: TeamMetrics }>(`/reports/agents?days=${days}`),
      get<{ rows: QueueMetrics[] }>(`/reports/queues?days=${days}`),
      get<{ rows: Offender[] }>('/reports/offenders'),
    ]);
    daily = d.rows;
    hours = h.rows;
    agents = a.rows;
    team = a.team;
    queuesRows = q.rows;
    offenders = o.rows;
  },
  view() {
    const t = team;
    const total = daily.reduce((n, d) => n + d.timeouts, 0);
    const deact = daily.reduce((n, d) => n + d.deactivations, 0);
    const aCols = t ? agentColumns(t) : [];
    const qCols = t ? queueColumns(t) : [];
    const needle = search.trim().toLowerCase();
    const matching = sortRows(needle ? agents.filter((r) => agentName(r).toLowerCase().includes(needle)) : agents, aCols, agentSort);
    const shown = showAll || needle ? matching : matching.slice(0, AGENT_ROWS);
    return html`
      <div class="row-between page-head">
        <h1>Reports ${helpLink('reports')}</h1>
        ${periodSelect(reports.load)}
      </div>
      ${t ? teamKpis(t) : ''}
      ${t?.data_since
        ? html`<p class="hint">Wrap-up data since ${when(t.data_since)}. Close calls use each queue's ACW limit, learned from its timeouts
            (${duration(t.acw_limit_seconds)} for a queue with none yet).</p>`
        : ''}

      <section class="panel">
        <h2>Timeouts per day</h2>
        <p class="hint">${total} timeouts and ${deact} deactivations in the last ${daily.length} days.</p>
        <div class="legend"><span><i class="sw s1"></i>Timeouts</span><span><i class="sw s2"></i>Deactivations</span></div>
        ${dailyChart()}
      </section>

      <section class="panel">
        <h2>Timeouts by hour of day</h2>
        <p class="hint">Last ${periodName()}, in the report time zone. Hover over an hour for its wrap-ups and timeout rate.</p>
        ${hourChart('hours', hours)}
      </section>

      <section>
        <div class="row-between">
          <h2>By agent</h2>
          <input type="search" class="compact" placeholder="Find an agent" aria-label="Find an agent" .value=${search}
            @input=${(e: Event) => { search = (e.target as HTMLInputElement).value; app.rerender(); }}>
        </div>
        <p class="hint">Every agent who wrapped up in the last ${periodName()}. Select a column heading to sort, or an agent for their details.</p>
        <div class="table-wrap"><table class="metrics">
          <thead>${sortHeader(aCols, agentSort)}</thead>
          <tbody>${shown.length
            ? shown.map((r) => html`<tr>${aCols.map((c) => html`<td class=${c.num ? 'num' : ''}>${c.cell(r)}</td>`)}</tr>`)
            : emptyRow(Math.max(1, aCols.length), needle ? 'No agent matches.' : 'No data yet.')}</tbody>
        </table></div>
        ${!needle && matching.length > AGENT_ROWS
          ? html`<p><button class="btn quiet compact" @click=${() => { showAll = !showAll; app.rerender(); }}>
              ${showAll ? `Show the first ${AGENT_ROWS}` : `Show all ${matching.length} agents`}</button></p>`
          : ''}
      </section>

      <section>
        <h2>By queue</h2>
        <div class="table-wrap"><table class="metrics">
          <thead>${sortHeader(qCols, queueSort)}</thead>
          <tbody>${queuesRows.length
            ? sortRows(queuesRows, qCols, queueSort).map((r) => html`<tr>${qCols.map((c) => html`<td class=${c.num ? 'num' : ''}>${c.cell(r)}</td>`)}</tr>`)
            : emptyRow(Math.max(1, qCols.length), 'No data yet.')}</tbody>
        </table></div>
      </section>

      <section>
        <h2>Repeat offenders</h2>
        <p class="hint">Agents with 2 or more timeouts in the last 30 days.</p>
        <div class="table-wrap"><table>
          <thead><tr><th>Agent</th><th class="num">7 days</th><th class="num">30 days</th><th class="num">All time</th><th>Last timeout</th></tr></thead>
          <tbody>${offenders.length
            ? offenders.map((r) => html`<tr><td><a href=${agentHref(r.agent_id)}>${agentName(r)}</a></td><td class="num">${r.timeouts_7d}</td><td class="num">${r.timeouts_30d}</td>
                <td class="num">${r.timeouts_all_time}</td><td>${when(r.last_timeout_at)}</td></tr>`)
            : emptyRow(5, 'No repeat offenders.')}</tbody>
        </table></div>
      </section>`;
  },
};

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------
interface AuditRow { id: number; entity: string; entity_id: string; action: string; old_value: unknown; new_value: unknown; actor: string; occurred_at: string }
let auditRows: AuditRow[] = [];

const ENTITY: Record<string, string> = { settings: 'Rules', queue_policy: 'Queue', exemption: 'Exemption', admin_user: 'User' };

function change(r: AuditRow) {
  const oldV = (r.old_value ?? {}) as Record<string, unknown>;
  const newV = (r.new_value ?? {}) as Record<string, unknown>;
  const fmt = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  const keys = [...new Set([...Object.keys(oldV), ...Object.keys(newV)])].filter(
    (k) => !['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'version', 'queueId', 'agentId', 'email'].includes(k) && fmt(oldV[k]) !== fmt(newV[k]),
  );
  if (r.action === 'delete') return 'removed';
  if (r.action === 'emergency_stop') return `dry run on${newV.reason ? ` · reason: ${fmt(newV.reason)}` : ''}`;
  if (!keys.length) return r.action;
  return keys.map((k) => `${k}: ${fmt(oldV[k])} → ${fmt(newV[k])}`).join(' · ');
}

export const audit: Page = {
  title: 'Audit log',
  async load() {
    auditRows = (await get<{ audit: AuditRow[] }>('/audit?limit=200')).audit;
  },
  view() {
    return html`
      <h1>Audit log ${helpLink('audit')}</h1>
      <p class="hint">Every change to rules, queues, exemptions and users — who, when, and what changed. This log can't be edited.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>When</th><th>Who</th><th>What</th><th>Change</th></tr></thead>
        <tbody>${auditRows.length
          ? auditRows.map((r) => html`<tr><td class="nowrap">${when(r.occurred_at)}</td><td>${r.actor.replace(/^sso:/, '')}</td>
              <td>${ENTITY[r.entity] ?? r.entity}${r.entity !== 'settings' ? html`<br><code class="id">${r.entity_id}</code>` : ''}${r.action === 'emergency_stop' ? html` <span class="pill critical">emergency stop</span>` : ''}</td>
              <td>${change(r)}</td></tr>`)
          : emptyRow(4, 'No changes yet.')}</tbody>
      </table></div>`;
  },
};
